// src/features/OrdineDiretto.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../components/hooks/hooks-e-commerce/useAuth';
import useCart from '../components/hooks/hooks-e-commerce/useCart';
import useAddressUser from '../components/hooks/hooks-e-commerce/useAddressUser';
import InternationalAddressForm from './InternationalAddressForm';
import { orderService } from '../components/services/orderServices';

import iconPagSecuroBlk from '../components/images/icons/iconPagSecuroBlk50.png';
import iconDeliveryBlk from '../components/images/icons/iconDeliveryBlackW60.png';
import iconPackagingBlk from '../components/images/icons/iconPackagingW48.png';
import './ordine-diretto.scss';

// Costanti per la spedizione
const SHIPPING_COST_PER_BOTTLE = 3.40;

function OrdineDiretto() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cart, loading, error, refetch } = useCart();
    const { defaultAddress, fetchAddresses } = useAddressUser();
    const [imageErrors, setImageErrors] = useState({});
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isAddressModified, setIsAddressModified] = useState(false);
    const [isDefaultAddressLoaded, setIsDefaultAddressLoaded] = useState(false);
    
    console.log('🛒 Cart completo in OrdineDiretto:', cart);

    // Stato per il form
    const [sameAsShipping, setSameAsShipping] = useState(true);
    const [shippingValid, setShippingValid] = useState(false);
    const [billingValid, setBillingValid] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBillingFields, setShowBillingFields] = useState(false);

    // useRef per evitare loop
    const isInitialized = useRef(false);
    // Stato per tracciare se l'email è valida
    const [isEmailValid, setIsEmailValid] = useState(false);

    // Questa funzione riceve il booleano
    const handleShippingValidation = (section, isValid) => {
        console.log(`📋 Ricevuto da ${section}:`, isValid, "Tipo:", typeof isValid);
        setShippingValid(isValid);
    };

    const handleBillingValidation = (section, isValid) => {
        console.log(`📋 Ricevuto da ${section}:`, isValid, "Tipo:", typeof isValid);
        setBillingValid(isValid);
    };

    const handleEmailChange = (e) => {
        const email = e.target.value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = emailRegex.test(email);
        
        setFormData(prev => ({
            ...prev,
            shipping: { ...prev.shipping, email: email }
        }));
        setIsEmailValid(isValid);
    };

    // Effetto per controllare quando abilitare il bottone
    const isFormValid = useMemo(() => {
        // ✅ CASO 1: Ha indirizzo predefinito e NON sta modificando → SEMPRE VALIDO
        if (defaultAddress && !isAddressModified && isDefaultAddressLoaded) {
            return true;
        }
        
        // ✅ CASO 2: Sta modificando O non ha predefinito → controlla i campi
        return isEmailValid && shippingValid && (sameAsShipping || billingValid);
        
    }, [defaultAddress, isAddressModified, isDefaultAddressLoaded, isEmailValid, shippingValid, sameAsShipping, billingValid]);

    const getFormError = useMemo(() => {
        // Se ha predefinito e non modifica → nessun errore
        if (defaultAddress && !isAddressModified && isDefaultAddressLoaded) {
            return null;
        }
        
        // Se sta modificando o non ha predefinito
        if (!isEmailValid) {
            return 'Inserisci un indirizzo email valido';
        }
        if (!shippingValid) {
            return 'Compila tutti i campi obbligatori dell\'indirizzo di spedizione';
        }
        if (!sameAsShipping && !billingValid) {
            return 'Compila tutti i campi obbligatori dell\'indirizzo di fatturazione';
        }
        return null;
    }, [defaultAddress, isAddressModified, isDefaultAddressLoaded, isEmailValid, shippingValid, sameAsShipping, billingValid]);

    // Stato dati form
    const [formData, setFormData] = useState({
        shipping: {
            email: '',
            nazione: 'IT',
            nome: '',
            cognome: '',
            azienda: '',
            indirizzo: '',
            indirizzo2: '',
            citta: '',
            provincia: '',
            cap: '',
            telefono: '',
            note: ''
        },
        billing: {
            nazione: 'IT',
            nome: '',
            cognome: '',
            azienda: '',
            indirizzo: '',
            indirizzo2: '',
            citta: '',
            provincia: '',
            cap: '',
            telefono: '',
            email: '',
            codFiscale: '',
            partitaIva: ''
        }
    });

    useEffect(() => {
        console.log("🔍 STATO BOTTONE:", {
            isSubmitting,
            shippingValid,
            sameAsShipping,
            billingValid,
            disabled: isSubmitting || !shippingValid || (!sameAsShipping && !billingValid)
        });
    }, [isSubmitting, shippingValid, sameAsShipping, billingValid]);

    // Carica gli indirizzi dell'utente all'avvio
    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    // Pre-compila il form con l'indirizzo predefinito
    useEffect(() => {
        if (defaultAddress && !isDefaultAddressLoaded && !isAddressModified) {
            console.log('📍 Indirizzo predefinito trovato:', defaultAddress);
            
            const email = defaultAddress.email || '';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const isValid = emailRegex.test(email);
            
            setFormData(prev => ({
                ...prev,
                shipping: {
                    ...prev.shipping,
                    nome: defaultAddress.nome || '',
                    cognome: defaultAddress.cognome || '',
                    azienda: defaultAddress.azienda || '',
                    indirizzo: defaultAddress.indirizzo || '',
                    indirizzo2: defaultAddress.indirizzo2 || '',
                    citta: defaultAddress.citta || '',
                    provincia: defaultAddress.provincia || '',
                    cap: defaultAddress.cap || '',
                    nazione: defaultAddress.nazione || 'IT',
                    telefono: defaultAddress.telefono || '',
                    email: email,
                    note: defaultAddress.note || ''
                }
            }));
            
            // ✅ IMPOSTA LA VALIDAZIONE EMAIL SUBITO
            setIsEmailValid(isValid);
            setShippingValid(true);
            setIsDefaultAddressLoaded(true);
        }
    }, [defaultAddress, isDefaultAddressLoaded, isAddressModified]);

    // Popola i dati dell'utente se loggato (solo una volta)
    useEffect(() => {
        if (user && !isInitialized.current) {
            setFormData(prev => ({
                ...prev,
                shipping: {
                    ...prev.shipping,
                    email: user.email_utente || '',
                    nome: user.nome_utente || '',
                    cognome: user.cognome_utente || '',
                    telefono: user.telefono_utente || ''
                }
            }));
            isInitialized.current = true;
        }
    }, [user]);

    // Calcolo totale bottiglie
    const getTotalBottles = useMemo(() => {
        if (!cart?.items) return 0;
        return cart.items.reduce((total, item) => total + (item.quantita || 0), 0);
    }, [cart?.items]);
    
    console.log('🍾 Numero bottiglie salvato:', getTotalBottles);

    // Calcolo costo spedizione
    const calculateShippingCost = useMemo(() => {
        return getTotalBottles * SHIPPING_COST_PER_BOTTLE;
    }, [getTotalBottles]);

    // Calcolo totale imballaggi (packaging)
    const calculateTotalPackaging = useMemo(() => {
        if (!cart?.items) return 0;
        return cart.items.reduce((total, item) => {
            return total + (item.packaging_cost || 0);
        }, 0);
    }, [cart?.items]);

    console.log('📦 Cost of packaging in OrdineDiretto:', calculateTotalPackaging);

    // Calcolo totale ordine (subtotal + spedizione)
    const calculateTotalOrder = useMemo(() => {
        const subtotal = cart?.subtotal || 0;
        return subtotal + calculateShippingCost + calculateTotalPackaging;
    }, [cart?.subtotal, calculateShippingCost, calculateTotalPackaging]);

    // Calcolo IVA
    const getIvaAliquota = (item) => {
        return item.aliquota_iva || 22.00;
    };
    
    const ivaCalculation = useMemo(() => {
        let ivaProdotti = 0;
        let imponibileProdotti = 0;
        
        cart?.items.forEach(item => {
            const aliquota = getIvaAliquota(item);
            const imponibile = (item.prezzo_unitario || 0) * (item.quantita || 0);
            imponibileProdotti += imponibile;
            ivaProdotti += imponibile * (aliquota / 100);
        });
        
        let maxAliquota = 22;
        cart?.items.forEach(item => {
            const aliquota = getIvaAliquota(item);
            if (aliquota > maxAliquota) maxAliquota = aliquota;
        });

        const imponibileImballaggio = calculateTotalPackaging;
        const ivaImballaggio = imponibileImballaggio * (maxAliquota / 100);
        
        const imponibileSpedizione = calculateShippingCost;
        const ivaSpedizione = imponibileSpedizione * (maxAliquota / 100);
        
        const totaleIva = ivaProdotti + ivaImballaggio + ivaSpedizione;
        
        return {
            ivaProdotti,
            ivaImballaggio,
            ivaSpedizione,
            totaleIva,
            maxAliquota,
            imponibileProdotti,
            imponibileImballaggio,
            imponibileSpedizione
        };
    }, [calculateShippingCost, calculateTotalPackaging, cart?.items]);

    // Gestione cambio dati shipping (senza loop)
    const handleShippingDataChange = useCallback((section, data) => {
        setFormData(prev => ({
            ...prev,
            shipping: { ...prev.shipping, ...data }
        }));
        setIsAddressModified(true);
    }, []);
    
    // Gestione cambio dati billing
    const handleBillingDataChange = useCallback((section, data) => {
        setFormData(prev => ({
            ...prev,
            billing: { ...prev.billing, ...data }
        }));
    }, []);

    // Gestione checkbox "dati coincidenti"
    const handleSameAsShippingChange = useCallback((e) => {
        const isChecked = e.target.checked;
        setSameAsShipping(isChecked);
        
        if (isChecked) {
            setFormData(prev => ({
                ...prev,
                billing: {
                    ...prev.billing,
                    nome: prev.shipping.nome,
                    cognome: prev.shipping.cognome,
                    azienda: prev.shipping.azienda,
                    indirizzo: prev.shipping.indirizzo,
                    indirizzo2: prev.shipping.indirizzo2,
                    citta: prev.shipping.citta,
                    provincia: prev.shipping.provincia,
                    cap: prev.shipping.cap,
                    nazione: prev.shipping.nazione,
                    telefono: prev.shipping.telefono,
                    codFiscale: prev.billing.codFiscale,
                    partitaIva: prev.billing.partitaIva
                }
            }));
            setShowBillingFields(false);
        }
    }, []);

    // Funzione per gestire la checkbox "mostra campi fatturazione"
    const handleShowBillingFieldsChange = (e) => {
        const isChecked = e.target.checked;
        setShowBillingFields(isChecked);
        
        if (!isChecked) {
            setFormData(prev => ({
                ...prev,
                billing: {
                    ...prev.billing,
                    codFiscale: '',
                    partitaIva: ''
                }
            }));
        }
    };

    // Gestione campi fiscali
    const handleFiscalChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            billing: {
                ...prev.billing,
                [field]: value
            }
        }));
    };

    const handleBillingFiscalChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            billing: {
                ...prev.billing,
                [field]: value
            }
        }));
    };

    // ✅ Gestisce il click su "Modifica"
    const handleModifyClick = () => {
        // ✅ Resetta la validazione del form di spedizione
        setShippingValid(false);
        setIsAddressModified(true);
    };

    // Ripristina l'indirizzo predefinito
    const resetToDefaultAddress = useCallback(() => {
        if (defaultAddress) {
            const email = defaultAddress.email || '';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            
            setFormData(prev => ({
                ...prev,
                shipping: {
                    ...prev.shipping,
                    nome: defaultAddress.nome || '',
                    cognome: defaultAddress.cognome || '',
                    azienda: defaultAddress.azienda || '',
                    indirizzo: defaultAddress.indirizzo || '',
                    indirizzo2: defaultAddress.indirizzo2 || '',
                    citta: defaultAddress.citta || '',
                    provincia: defaultAddress.provincia || '',
                    cap: defaultAddress.cap || '',
                    nazione: defaultAddress.nazione || 'IT',
                    telefono: defaultAddress.telefono || '',
                    email: email,
                    note: defaultAddress.note || ''
                }
            }));
            
            // ✅ RIPRISTINA LA VALIDAZIONE
            setIsEmailValid(emailRegex.test(email));
            setShippingValid(true);
            setIsAddressModified(false);
        }
    }, [defaultAddress]);

    // Submit del form
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validazione CF se richiesta fattura
        if (sameAsShipping && showBillingFields) {
            if (!formData.billing.codFiscale?.trim()) {
                alert('Il Codice Fiscale è obbligatorio per la fattura');
                return;
            }
            const cfRegex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;
            if (!cfRegex.test(formData.billing.codFiscale)) {
                alert('Codice Fiscale non valido');
                return;
            }
            if (formData.billing.partitaIva && !/^\d{11}$/.test(formData.billing.partitaIva)) {
                alert('Partita IVA non valida');
                return;
            }
        }
        
        // Validazione campi obbligatori
        if (!shippingValid || (!sameAsShipping && !billingValid)) {
            alert('Compila tutti i campi obbligatori');
            return;
        }
        
        setIsSubmitting(true);

        const userId = user?.id || cart?.id_utente;
        console.log('🔍 userId utilizzato:', userId);

        if (!userId) {
            alert('Errore: utente non identificato');
            setIsSubmitting(false);
            return;
        }
    
        try {
            // ✅ DETERMINA INDIRIZZO DA USARE
            let indirizzoSpedizioneRaw;
            const useDefault = !isAddressModified && defaultAddress;
            
            if (useDefault) {
                console.log('♻️ Indirizzo NON modificato, uso il predefinito');
                indirizzoSpedizioneRaw = {
                    nome: defaultAddress.nome || '',
                    cognome: defaultAddress.cognome || '',
                    azienda: defaultAddress.azienda || null,
                    indirizzo: defaultAddress.indirizzo || '',
                    indirizzo2: defaultAddress.indirizzo2 || '',
                    citta: defaultAddress.citta || '',
                    provincia: defaultAddress.provincia || '',
                    cap: defaultAddress.cap || '',
                    nazione: defaultAddress.nazione || 'Italia',
                    telefono: defaultAddress.telefono || '',
                    email: defaultAddress.email || formData.shipping.email || '',
                    note: defaultAddress.note || null
                };
            } else {
                console.log('🆕 Indirizzo MODIFICATO, salverò come nuovo');
                indirizzoSpedizioneRaw = {
                    nome: formData.shipping.nome,
                    cognome: formData.shipping.cognome,
                    azienda: formData.shipping.azienda || null,
                    indirizzo: formData.shipping.indirizzo,
                    indirizzo2: formData.shipping.indirizzo2 || '',
                    citta: formData.shipping.citta,
                    provincia: formData.shipping.provincia,
                    cap: formData.shipping.cap,
                    nazione: formData.shipping.nazione || 'Italia',
                    telefono: formData.shipping.telefono,
                    email: formData.shipping.email,
                    note: formData.shipping.note || null
                };
            }
            
            // 1. PREPARA INDIRIZZO SPEDIZIONE
            let indirizzoSpedizioneObj = {
                ...indirizzoSpedizioneRaw,
                indirizzo2: indirizzoSpedizioneRaw.indirizzo2 || ''
            };
            
            // 2. PREPARA INDIRIZZO FATTURAZIONE
            let indirizzoFatturazioneObj = null;
        
            // ✅ CASO 1: L'utente ha DESELEZIONATO "stessi dati di fatturazione"
            if (!sameAsShipping) {
                indirizzoFatturazioneObj = {
                    nome: formData.billing.nome,
                    cognome: formData.billing.cognome,
                    azienda: formData.billing.azienda || null,
                    indirizzo: formData.billing.indirizzo,
                    indirizzo2: formData.billing.indirizzo2 || '',
                    citta: formData.billing.citta,
                    provincia: formData.billing.provincia,
                    cap: formData.billing.cap,
                    nazione: formData.billing.nazione || 'Italia',
                    telefono: formData.billing.telefono,
                    email: formData.billing.email || formData.shipping.email,
                    note: null,
                    // ✅ DATI FISCALI INCLUSI
                    codFiscale: formData.billing.codFiscale || null,
                    partitaIva: formData.billing.partitaIva || null
                };
            } 
            // ✅ CASO 2: Checkbox "stessi dati" SPUNTATA ma con dati fiscali
            else if (sameAsShipping && showBillingFields && formData.billing.codFiscale) {
                indirizzoFatturazioneObj = {
                    ...indirizzoSpedizioneObj,
                    // ✅ DATI FISCALI AGGIUNTI ALL'INDIRIZZO DI SPEDIZIONE
                    codFiscale: formData.billing.codFiscale || null,
                    partitaIva: formData.billing.partitaIva || null
                };
                console.log('📄 Dati fiscali aggiunti all\'indirizzo di spedizione');
        
            } else {
                // ✅ Se la checkbox è spuntata, NON passare alcun indirizzo di fatturazione
                console.log('ℹ️ Indirizzo di fatturazione NON fornito');
                indirizzoFatturazioneObj = null;
            }

            // Calcolo sconto Abruzzo
            const provincia = indirizzoSpedizioneObj.provincia?.toUpperCase();
            const isAbruzzo = ['AQ', 'CH', 'PE', 'TE'].includes(provincia);

            const costoSenzaSconto = calculateShippingCost;
            let scontoSpedizione = 0;
            let costoSpedizioneScontato = costoSenzaSconto;

            if (isAbruzzo) {
                scontoSpedizione = costoSenzaSconto * 0.5;
                costoSpedizioneScontato = costoSenzaSconto - scontoSpedizione;
                console.log(`🎉 Sconto Abruzzo: -€${scontoSpedizione}`);
            }

            // 5. CALCOLO IVA CORRETTO
            const ivaPercentuale = 22;

            // Prodotti
            const imponibileProdotti = cart.subtotal * (ivaPercentuale / 100);
            const ivaProdotti = imponibileProdotti;

            // Spedizione
            const imponibileSpedizione = costoSpedizioneScontato * (ivaPercentuale / 100);
            const ivaSpedizione = imponibileSpedizione;

            // Imballaggio
            const imponibileImballaggio = calculateTotalPackaging * (ivaPercentuale / 100);
            const ivaImballaggio = imponibileImballaggio;

            // Totali
            const ivaTotale = ivaProdotti + ivaSpedizione + ivaImballaggio;
            const importoTotale = cart.subtotal + costoSpedizioneScontato + calculateTotalPackaging;
            
            // 6. GENERA NUMERO ORDINE
            const numeroOrdine = `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            
            // 7. PREPARA DATI ORDINE
            const orderData = {
                id_utente: userId,
                indirizzo_spedizione: indirizzoSpedizioneObj,
                indirizzo_fatturazione: indirizzoFatturazioneObj,
                isAddressModified: isAddressModified || false,
                subtotale: cart.subtotal,
                costo_spedizione: costoSpedizioneScontato,
                spese_imballaggio: calculateTotalPackaging,
                importo_iva: ivaTotale,
                importo_totale: importoTotale,
                sconto_spedizione: scontoSpedizione,
                sconto_tipo: isAbruzzo ? 'abruzzo_50' : null,
                numero_bottiglie: getTotalBottles,
                cod_fiscale: formData.billing.codFiscale || null,
                partita_iva: formData.billing.partitaIva || null,
                note_cliente: formData.shipping.note || null,
                metodo_spedizione: 'standard',
                metodo_pagamento: null,
                stato: 'in_attesa',
                stato_pagamento: 'in_attesa',
                numero_ordine: numeroOrdine
            };
            
            console.log('📦 orderData inviato al backend:', {
                ...orderData,
                sconto_spedizione: orderData.sconto_spedizione,
                sconto_tipo: orderData.sconto_tipo,
                numero_bottiglie: orderData.numero_bottiglie
            });
            
            console.log('📦 Ordine da salvare:', orderData);
            
            // 8. SALVA ORDINE NEL DATABASE
            const response = await orderService.createOrder(orderData);
            
            console.log('✅ Ordine creato con successo:', response.order);
            
            // 9. REINDIRIZZA AL PAGAMENTO
            navigate('/negozioonline/checkout/ordinepagamento', {
                state: {
                    order: response.order,
                    numeroOrdine: response.order.numero_ordine,
                    indirizzo_spedizione: indirizzoSpedizioneObj,
                    indirizzo_fatturazione: indirizzoFatturazioneObj
                }
            });
            
        } catch (error) {
            console.error('❌ Errore durante il salvataggio:', error);
            alert(error.message || 'Si è verificato un errore. Riprova più tardi.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // useEffect per gestire il resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1100) {
                setIsCartOpen(true);
            } else {
                setIsCartOpen(false);
            }
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (window.innerWidth <= 1100) {
            if (isCartOpen) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [isCartOpen]);

    useEffect(() => {
        console.log('🔍 Stato validazione:', {
            isEmailValid,
            shippingValid,
            sameAsShipping,
            billingValid,
            isFormValid: isEmailValid && shippingValid && (sameAsShipping || billingValid)
        });
    }, [isEmailValid, shippingValid, sameAsShipping, billingValid]);

    const deliveryDate = useMemo(() => {
        const date = new Date();
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + 2, 3, 0, 0));
    }, []);

    // Mostra loading state mentre i dati vengono caricati
    if (loading) {
        return (
            <section>
                <div className='ordine-diretto loading-container'>
                    <div className="loading-spinner"></div>
                    <p>Caricamento carrello...</p>
                </div>
            </section>
        );
    }

    // Gestione errori caricamento carrello
    if (error) {
        return (
            <section>
                <div className='ordine-diretto error-container'>
                    <div className="error-message">
                        <p>⚠️ Si è verificato un errore nel caricamento del carrello</p>
                        <p className="error-details">{error.message || 'Errore sconosciuto'}</p>
                        {refetch && (
                            <button 
                                onClick={() => refetch()} 
                                className="retry-button"
                            >
                                Riprova
                            </button>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    if (!cart) {
        return (
            <section>
                <div className='ordine-diretto empty-cart'>
                    <p>🛒 Nessun carrello attivo trovato</p>
                    <p className="empty-cart-hint">Aggiungi prodotti per iniziare il tuo ordine</p>
                </div>
            </section>
        );
    }

    // Gestione errore immagine
    const handleImageError = (productId, e) => {
        setImageErrors(prev => ({
            ...prev,
            [productId]: true
        }));
        console.error(`Errore caricamento immagine per prodotto ${productId}:`, e.target.src);
    };

    return (
        <section>
            <div className='ordine-diretto'>
                <form className='od-formulario' onSubmit={handleSubmit}>
                    <p className="od-form__title">Dati di consegna</p>
                    
                    {/* Banner indirizzo predefinito */}
                    {defaultAddress && !isAddressModified && isDefaultAddressLoaded && (
                        <div className="default-address-banner">
                            <span className="badge-default">⭐ Indirizzo predefinito</span>
                            <p className="default-address-text">
                                {defaultAddress.nome} {defaultAddress.cognome} - {defaultAddress.indirizzo}, {defaultAddress.citta}
                            </p>
                            <button 
                                type="button" 
                                className="modify-address-btn"
                                onClick={handleModifyClick}
                            >
                                ✏️ Modifica
                            </button>
                        </div>
                    )}
                    
                    {/* Email separata (campo comune) */}
                    <div className="form-group">
                        <label htmlFor="shipping-email">Email *</label>
                        <input
                            type="email"
                            id="shipping-email"
                            value={formData.shipping.email || ''}
                            onChange={handleEmailChange}
                            className={!isEmailValid && formData.shipping.email ? 'error' : ''}
                            required
                        />
                        {!isEmailValid && formData.shipping.email && (
                            <span className="error-message">Email non valida</span>
                        )}
                    </div>
                    
                    {/* Form indirizzo internazionale per spedizione */}
                    {(isAddressModified || !defaultAddress) && (
                        <InternationalAddressForm
                            section="shipping"
                            externalFormData={formData.shipping}
                            onDataChange={handleShippingDataChange}
                            onValidationChange={handleShippingValidation}
                        />
                    )}
                    
                    {/* Pulsante per ripristinare l'indirizzo predefinito */}
                    {defaultAddress && isAddressModified && (
                        <button 
                            type="button" 
                            className="reset-default-btn--color"
                            onClick={resetToDefaultAddress}
                        >
                            <span className="reset-icon">🔄</span>
                            Ripristina indirizzo predefinito
                        </button>
                    )}
                    
                    {/* Dati fatturazione */}
                    <div className="form-section billing-section">
                        <div className="same-as-shipping">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={sameAsShipping}
                                    onChange={handleSameAsShippingChange}
                                />
                                <span>I dati di fatturazione sono gli stessi della consegna</span>
                            </label>
                        </div>
                        
                        {sameAsShipping ? (
                            <>
                                {/* Checkbox secondaria: mostra campi fiscali */}
                                <div className="show-billing-fields">
                                    <label className="checkbox-label--secondary">
                                        <input
                                            type="checkbox"
                                            checked={showBillingFields}
                                            onChange={handleShowBillingFieldsChange}
                                        />
                                        <span>📄 Aggiungi dati per fattura (Codice Fiscale, Partita IVA)</span>
                                    </label>
                                    <small className="form-hint">
                                        Necessario solo se desideri una fattura intestata. Per una semplice ricevuta non serve.
                                    </small>
                                </div>
                                
                                {/* Campi fiscali (visibili solo se showBillingFields = true) */}
                                {showBillingFields && (
                                    <div className="billing-fiscal-fields fade-in">
                                        <p className="od-form__subtitle">Dati per la fatturazione</p>
                                        <p className="form-note">
                                            L'indirizzo di fatturazione corrisponde a quello di spedizione.
                                            Inserisci solo i dati fiscali necessari.
                                        </p>
                                        
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="billing-codFiscale">
                                                    Codice Fiscale <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="billing-codFiscale"
                                                    maxLength="16"
                                                    placeholder="RSSMRA85M10B123F"
                                                    value={formData.billing.codFiscale || ''}
                                                    onChange={(e) => handleFiscalChange('codFiscale', e.target.value.toUpperCase())}
                                                />
                                                <small className="form-hint">Obbligatorio per l'emissione della fattura</small>
                                            </div>
                                            
                                            <div className="form-group">
                                                <label htmlFor="billing-partitaIva">Partita IVA (opzionale)</label>
                                                <input
                                                    type="text"
                                                    id="billing-partitaIva"
                                                    maxLength="11"
                                                    placeholder="12345678901"
                                                    value={formData.billing.partitaIva || ''}
                                                    onChange={(e) => handleFiscalChange('partitaIva', e.target.value.replace(/\D/g, ''))}
                                                />
                                                <small className="form-hint">Solo se in possesso di Partita IVA (11 cifre)</small>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            // ========== CASO 2: DATI DIVERSI ==========
                            <>
                                <p className="od-form__title">Dati di fatturazione</p>
                                
                                {/* Form indirizzo internazionale per fatturazione */}
                                <InternationalAddressForm
                                    section="billing"
                                    externalFormData={formData.billing}
                                    onDataChange={handleBillingDataChange}
                                    onValidationChange={handleBillingValidation}
                                />
                                
                                {/* Campi fiscali aggiuntivi (sempre visibili quando dati diversi) */}
                                <div className="billing-fiscal-fields">
                                    <p className="od-form__subtitle">Dati fiscali</p>
                                    
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="billing-codFiscale-diff">
                                                Codice Fiscale <span className="required">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="billing-codFiscale-diff"
                                                maxLength="16"
                                                placeholder="RSSMRA85M10B123F"
                                                value={formData.billing.codFiscale || ''}
                                                onChange={(e) => handleBillingFiscalChange('codFiscale', e.target.value.toUpperCase())}
                                            />
                                            <small className="form-hint">Obbligatorio per l'emissione della fattura</small>
                                        </div>
                                        
                                        <div className="form-group">
                                            <label htmlFor="billing-partitaIva-diff">Partita IVA (opzionale)</label>
                                            <input
                                                type="text"
                                                id="billing-partitaIva-diff"
                                                maxLength="11"
                                                placeholder="12345678901"
                                                value={formData.billing.partitaIva || ''}
                                                onChange={(e) => handleBillingFiscalChange('partitaIva', e.target.value.replace(/\D/g, ''))}
                                            />
                                            <small className="form-hint">Solo se in possesso di Partita IVA (11 cifre)</small>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    {/* Note (opzionale) */}
                    <div className="form-group">
                        <label htmlFor="shipping-note">Note (opzionale)</label>
                        <textarea
                            id="shipping-note"
                            rows="3"
                            value={formData.shipping.note || ''}
                            onChange={(e) => handleShippingDataChange('shipping', { note: e.target.value })}
                            placeholder="Indicazioni per il corriere, orari di consegna, ecc."
                        />
                    </div>
                    
                    <button 
                        type="submit" 
                        className="submit-button"
                        disabled={isSubmitting || !isFormValid}
                    >
                        {isSubmitting ? 'Elaborazione...' : `Completa ordine - € ${calculateTotalOrder.toFixed(2)}`}
                    </button>
                    
                    {/* Messaggio informativo */}
                    {!isFormValid && (
                        <div className="form-notice warning">
                            <span>⚠️</span>
                            <p>{getFormError}</p>
                        </div>
                    )}
                    
                    <div className="cart-delevery realistic mt-1">
                        <div className="delevery-termini mt-12">
                            <img src={iconPackagingBlk} alt="icon of packaging" />
                            <p className='termini__infos'>Sicura antiurto</p>
                        </div>
                        <div className="delevery-termini">
                            <img src={iconPagSecuroBlk} alt="icon of paiement secure" />
                            <p className='termini__infos'>100% Sicuro</p>
                        </div>
                        <div className="delevery-termini mb-12">
                            <img src={iconDeliveryBlk} alt="icon of delais for delivery" />
                            <p className='termini__infos'>Consegnato il: {deliveryDate.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                    </div>
                </form>

                {/* Pulsante toggle per schermi ≤ 1100px */}
                <button 
                    className={`cart-toggle-btn ${isCartOpen ? 'active' : ''}`}
                    onClick={() => setIsCartOpen(!isCartOpen)}
                    aria-label="Toggle carrello"
                >
                    <span className="cart-toggle-icon">
                        {isCartOpen ? (
                            '✕'
                        ) : (
                            <svg 
                                className="cart-icon"
                                viewBox="0 0 24 24"
                                fill="#edf5e1"
                                width="20"
                                height="20"
                            >
                                <path d="M7 4H5L4 6v2h2l3.6 7.59-1.35 2.44A2 2 0 0 0 10 22h10v-2H10l1.1-2h7.45a2 2 0 0 0 1.75-1.03l3.58-6.49A1 1 0 0 0 23 9H8.21L7 4z"/>
                            </svg>
                        )}
                    </span>

                    {cart?.item_count > 0 && !isCartOpen && (
                        <span className="cart-badge">{cart.item_count}</span>
                    )}
                </button>

                <div className={`od-cart ${isCartOpen ? 'open' : ''}`}>
                    <div className="od-cart-header">
                        <p className="od-cart__title">Riepilogo del tuo carrello</p>
                        {/*  <button 
                            className="cart-close-btn"
                            onClick={() => setIsCartOpen(false)}
                            aria-label="Chiudi carrello"
                        >
                            ✕
                        </button> */}
                    </div>
                    
                    {cart.items.map((elt) => {
                        const totalPrice = elt.prezzo_totale || (elt.prezzo_unitario * elt.quantita);
                        
                        return (
                            <div className="od-cart-article" key={elt.id}>
                                <div className="od-article__identity">
                                    {!imageErrors[elt.id] && elt.img_cartart ? (
                                        <img 
                                            className='od-cart-article__img' 
                                            src={require(`../shop/components/images/cart/${elt.img_cartart}.png`)} 
                                            alt={elt.nome_prodotto || 'Prodotto'}
                                            onError={(e) => handleImageError(elt.id, e)}
                                        />
                                    ) : (
                                        <div className="od-cart-article__img-placeholder">
                                            <span>📦</span>
                                        </div>
                                    )}
                                    
                                    <div className="od-article__infos">
                                        <p className="infos__cart">
                                            <strong>{elt.nome_prodotto || 'Prodotto sconosciuto'}</strong> 
                                            {elt.denominazione && ` - ${elt.denominazione}`}
                                        </p>
                                        <p className="infos__cart">
                                            <strong>{elt.tipo_vino || 'Vino'}</strong> 
                                            {elt.annata && ` - ${elt.annata}`}
                                        </p>
                                        <p className="infos__cart">
                                            € {Number(elt.prezzo_unitario || 0).toFixed(2)}<strong> - Per unità</strong>
                                        </p>
                                        <p className="infos__cart">
                                            Quantità: <strong>{elt.quantita || 0}</strong>
                                        </p>
                                        {elt.packaging_cost > 0 && (
                                            <p className="infos__cart packaging-info">
                                                <span>📦</span> Costo imballaggio: € {Number(elt.packaging_cost).toFixed(2)}
                                            </p>
                                        )}
                                        {elt.packaging_combo && elt.packaging_combo.length > 0 && (
                                            <p className="infos__cart packaging-info">
                                                Confezione: {elt.packaging_description || `${elt.packaging_combo.join(' × ')} unità`}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="od-article__total">
                                    <p>
                                        <strong>Totale: € {Number(totalPrice).toFixed(2)}</strong>
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                    
                    <div className="od-cart__subtotal">
                        <div className="subtotal-details mb-1">
                            <p>
                                <strong>Subtotale: € {Number(cart.subtotal || 0).toFixed(2)}</strong>
                            </p>
                            {cart.item_count && (
                                <p className="items-count">
                                    {cart.item_count} {cart.item_count === 1 ? 'articolo' : 'articoli'} nel carrello
                                </p>
                            )}
                        </div>

                        <div className="summary-item__shipping">
                            <div className="shipping-cost">
                                <span>Spedizione:</span>
                                <span>€ {calculateShippingCost.toFixed(2)}</span>
                            </div>
                            <p className='termini__infos'>Consegna prevista: {deliveryDate.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>

                        <div className="summary-final__cost">
                            {calculateTotalPackaging > 0 && (
                                <div className="summary-item__packaging mt-1">
                                    <div className="packaging-cost">
                                        <span>Imballaggio:</span>
                                        <span>€ {calculateTotalPackaging.toFixed(2)}</span>
                                    </div>
                                    <p>{cart.items.filter(item => item.packaging_cost > 0).length} articoli con imballaggio</p>
                                </div>
                            )}

                            <div className="total-details mt-2">
                                <p>
                                    <strong>Totale ordine: € {calculateTotalOrder.toFixed(2)}</strong>
                                </p>
                                {cart.item_count && (
                                    <p className="items-count">
                                        {cart.item_count} {cart.item_count === 1 ? 'articolo' : 'articoli'} nel carrello
                                        {getTotalBottles > 0 && ` (${getTotalBottles} bottiglie totali)`}
                                    </p>
                                )}
                                <p className="od-summary__iva-total">
                                    <span>IVA totale:</span>
                                    <span>€ {ivaCalculation.totaleIva.toFixed(2)}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default OrdineDiretto;